import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import notificationService from '../services/notificationService';
import { useAuth } from './AuthContext';
import {
  announceJobFeedApproval,
  announceJobFeedSync,
  isJobFeedApprovalNotification,
  isJobFeedSyncNotification,
} from '../services/jobFeedApprovalService';

const NotificationContext = createContext();

// Action types
const NOTIFICATION_ACTIONS = {
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  SET_COUNTS: 'SET_COUNTS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  MARK_AS_READ: 'MARK_AS_READ',
  MARK_ALL_AS_READ: 'MARK_ALL_AS_READ'
};

// Initial state
const initialState = {
  notifications: [],
  counts: {
    total: 0,
    unread: 0,
    byType: {}
  },
  isLoading: false,
  error: null,
  hasMore: false
};

// Reducer
function notificationReducer(state, action) {
  switch (action.type) {
    case NOTIFICATION_ACTIONS.SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.payload.notifications,
        hasMore: action.payload.hasMore,
        isLoading: false,
        error: null
      };

    case NOTIFICATION_ACTIONS.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        counts: {
          ...state.counts,
          total: state.counts.total + 1,
          unread: action.payload.isRead ? state.counts.unread : state.counts.unread + 1,
          byType: {
            ...state.counts.byType,
            [action.payload.type]: (state.counts.byType[action.payload.type] || 0) + 1
          }
        }
      };

    case NOTIFICATION_ACTIONS.UPDATE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload.id ? { ...n, ...action.payload } : n
        )
      };

    case NOTIFICATION_ACTIONS.REMOVE_NOTIFICATION:
      const removedNotification = state.notifications.find(n => n.id === action.payload);
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
        counts: {
          ...state.counts,
          total: Math.max(0, state.counts.total - 1),
          unread: removedNotification && !removedNotification.isRead 
            ? Math.max(0, state.counts.unread - 1) 
            : state.counts.unread,
          byType: {
            ...state.counts.byType,
            [removedNotification?.type]: Math.max(0, (state.counts.byType[removedNotification?.type] || 0) - 1)
          }
        }
      };

    case NOTIFICATION_ACTIONS.SET_COUNTS:
      const payload = action.payload || {};
      const prevCounts = state.counts || {};
      return {
        ...state,
        counts: {
          total: typeof payload.total === 'number' ? payload.total : (typeof prevCounts.total === 'number' ? prevCounts.total : 0),
          unread: typeof payload.unread === 'number' ? payload.unread : (typeof prevCounts.unread === 'number' ? prevCounts.unread : 0),
          byType: payload.byType && typeof payload.byType === 'object' ? payload.byType : (prevCounts.byType && typeof prevCounts.byType === 'object' ? prevCounts.byType : {})
        }
      };

    case NOTIFICATION_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case NOTIFICATION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };

    case NOTIFICATION_ACTIONS.MARK_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, isRead: true } : n
        ),
        counts: {
          ...state.counts,
          unread: Math.max(0, state.counts.unread - 1)
        }
      };

    case NOTIFICATION_ACTIONS.MARK_ALL_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map(n => ({ ...n, isRead: true })),
        counts: {
          ...state.counts,
          unread: 0
        }
      };

    default:
      return state;
  }
}

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const wsRef = useRef(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Load initial data only when authenticated
  useEffect(() => {
    // Don't load notifications if still loading auth or not authenticated
    if (authLoading || !isAuthenticated) {
      return;
    }

    loadNotifications();
    loadNotificationCounts();
    
    // Subscribe to real-time notifications
    wsRef.current = notificationService.subscribeToNotifications(
      handleNewNotification,
      handleWebSocketError
    );

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isAuthenticated, authLoading]);

  const loadNotifications = async (page = 1, params = {}) => {
    try {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_LOADING, payload: true });
      const response = await notificationService.getNotifications({ 
        page, 
        limit: 20,
        ...params 
      });
      
      dispatch({
        type: NOTIFICATION_ACTIONS.SET_NOTIFICATIONS,
        payload: {
          notifications: page === 1 ? response.notifications : [...state.notifications, ...response.notifications],
          hasMore: response.hasMore
        }
      });
    } catch (error) {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  const loadNotificationCounts = async () => {
    try {
      const counts = await notificationService.getNotificationCounts();
      dispatch({ type: NOTIFICATION_ACTIONS.SET_COUNTS, payload: counts });
    } catch (error) {
      console.error('Error loading notification counts:', error);
    }
  };

  const handleNewNotification = (notification) => {
    console.log('📨 Real-time notification received:', notification);
    dispatch({ type: NOTIFICATION_ACTIONS.ADD_NOTIFICATION, payload: notification });

    // The admin approval has already completed the first pull before this
    // payload is emitted. Invalidate the pre-pull feed/inventory snapshots and
    // hand navigation to the bridge mounted inside BrowserRouter.
    if (isJobFeedApprovalNotification(notification)) {
      announceJobFeedApproval(notification);
    } else if (isJobFeedSyncNotification(notification)) {
      announceJobFeedSync(notification);
    }
    
    // Show toast for billing notifications
    if (notification.type === 'billing') {
      const notificationType = notification.data?.notificationType;
      
      if (notificationType === 'subscription_upgraded') {
        toast.success(
          <div>
            <strong>{notification.title}</strong>
            <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
              {notification.message}
            </p>
          </div>,
          { autoClose: 8000 }
        );
      } else if (notificationType === 'eggs_purchased') {
        toast.success(
          <div>
            <strong>{notification.title}</strong>
            <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
              {notification.message}
            </p>
          </div>,
          { autoClose: 6000 }
        );
      } else if (notificationType === 'subscription_canceled') {
        toast.info(
          <div>
            <strong>{notification.title}</strong>
            <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
              {notification.message}
            </p>
          </div>,
          { autoClose: 8000 }
        );
      } else if (notificationType === 'subscription_renewed') {
        toast.success(
          <div>
            <strong>{notification.title}</strong>
            <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
              {notification.message}
            </p>
          </div>,
          { autoClose: 6000 }
        );
      }
    }

    // Show toast for credential/credentialing notifications (Nursys, NBCRNA, etc.)
    if (notification.type === 'credential_verification' || notification.type === 'credentialing') {
      const priority = notification.priority || 'info';
      const toastFn = priority === 'warning' ? toast.warning : toast.success;
      toastFn(
        <div>
          <strong>{notification.title}</strong>
          <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
            {notification.message}
          </p>
        </div>,
        { autoClose: 6000 }
      );
    }

    // Show toast for interview/negotiation state change notifications
    if (notification.type === 'negotiation') {
      const notifType = notification.data?.notificationType || '';
      const isInterviewRelated =
        notifType.includes('->interview') ||
        notifType.includes('interview->interview') ||
        notifType === 'interview_accepted_by_company' ||
        notification.title?.includes('Interview');

      if (isInterviewRelated) {
        const toastFn = notification.title?.includes('Accepted') || notification.title?.includes('Approved')
          ? toast.success
          : toast.info;
        toastFn(
          <div>
            <strong>{notification.title}</strong>
            <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
              {notification.message}
            </p>
          </div>,
          { autoClose: 6000 }
        );
      }
    }
  };

  const handleWebSocketError = (error) => {
    console.error('Notification WebSocket error:', error);
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      dispatch({ type: NOTIFICATION_ACTIONS.MARK_AS_READ, payload: notificationId });
    } catch (error) {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      dispatch({ type: NOTIFICATION_ACTIONS.MARK_ALL_AS_READ });
    } catch (error) {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      dispatch({ type: NOTIFICATION_ACTIONS.REMOVE_NOTIFICATION, payload: notificationId });
    } catch (error) {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  const refreshNotifications = () => {
    loadNotifications();
    loadNotificationCounts();
  };

  const value = {
    ...state,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
